import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Button,
  Chip,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Fab
} from '@material-ui/core';
import {
  Edit as EditIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Search as SearchIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import {
  fetchComponentsStart,
  setComponentSearchTerm,
  setComponentActiveFilter,
  setComponentPagination
} from '../../store/reducers/componentSlice';

const useStyles = makeStyles((theme) => ({
  searchContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
}));

const ComponentList: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const {
    components,
    componentLoading,
    componentError,
    componentPagination,
    componentSearchTerm,
    componentActiveFilter
  } = useSelector((state: RootState) => state.component);

  const [localSearchTerm, setLocalSearchTerm] = useState(componentSearchTerm);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    dispatch(fetchComponentsStart());
  }, [dispatch, componentPagination.currentPage, componentPagination.pageSize, componentSearchTerm, componentActiveFilter]);

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setComponentPagination({ ...componentPagination, currentPage: newPage }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setComponentPagination({
      ...componentPagination,
      currentPage: 0,
      pageSize: parseInt(event.target.value, 10)
    }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearchTerm(event.target.value);
  };

  const handleSearchSubmit = () => {
    dispatch(setComponentSearchTerm(localSearchTerm));
  };

  const handleActiveFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    const activeFilter = value === '' ? null : value === 'true';
    dispatch(setComponentActiveFilter(activeFilter));
  };

  const handleEdit = (id: string) => {
    navigate(`/components/edit/${id}`);
  };

  const handleAdd = () => {
    navigate('/components/add');
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  if (componentError) {
    return (
      <Box style={{ padding: 24 }}>
        <Typography color="error">Error: {componentError}</Typography>
      </Box>
    );
  }

  return (
    <Box style={{ padding: 24 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: 24 }}>
        <Typography variant="h4" component="h1">
          Components
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add New Component
        </Button>
      </Box>

      {/* Filters */}
      <Paper style={{ marginBottom: 16, padding: 16 }}>
        <Box className={classes.searchContainer}>
          <Box className={classes.searchBox}>
            <TextField
              size="small"
              placeholder="Search components..."
              value={localSearchTerm}
              onChange={handleSearchChange}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
              style={{ minWidth: 250 }}
            />
            <IconButton onClick={handleSearchSubmit} size="small">
              <SearchIcon />
            </IconButton>
            <IconButton onClick={toggleFilters} size="small">
              <FilterIcon />
            </IconButton>
          </Box>
        </Box>

        {showFilters && (
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={componentActiveFilter}
                  label="Status"
                  onChange={handleActiveFilterChange}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Components Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>UoM</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {componentLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : (!components || components.length === 0) ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No components found
                </TableCell>
              </TableRow>
            ) : (
              components.map((component) => (
                <TableRow key={component.id}>
                  <TableCell>{component.Name}</TableCell>
                  <TableCell>{component.Description}</TableCell>
                  <TableCell>{component.UoM?.Name || 'N/A'}</TableCell>
                  <TableCell>{component.Value}</TableCell>
                  <TableCell>
                    <Chip
                      label={component.Active ? 'Active' : 'Inactive'}
                      color={component.Active ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(component.id!)}
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={componentPagination.totalCount}
        rowsPerPage={componentPagination.pageSize}
        page={componentPagination.currentPage - 1}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        style={{
          position: 'fixed',
          bottom: 16,
          right: 16,
        }}
        onClick={handleAdd}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default ComponentList; 